import Box from "@mui/material/Box";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useAuth } from "modules/auth/auth.context";
import {
  ControlledDatePicker,
  ControlledFileUpload,
  ControlledSelect,
  ControlledTextField,
  ModalWrapper,
  SectionTitle,
} from "modules/shared/components";
import { logError } from "utils/logger";
import { getMyLeaveBalance, LeaveBalanceRow } from "../../people/people.api";
import {
  LEAVES_SESSION_OPTIONS,
  LEAVE_APPLICATION_DEFAULT_VALUES,
  LEAVE_TYPE_OPTIONS,
} from "../constants";
import { applyForLeave } from "../service";
import { calculateLeaveDuration } from "../utils";
import { leaveApplicationValidation } from "../utils/leaveApplication.validation";

/**
 * The balance row for the chosen leave type, matched by label (the options' labels are the
 * leave_types labels). Null when the type has no quota to check: unpaid leave, an untracked
 * type, or no row at all — the server still has the final say.
 */
function findCheckedBalance(balance: LeaveBalanceRow[], leaveType: string) {
  if (!leaveType) return null;
  const label =
    LEAVE_TYPE_OPTIONS.find((o) => o.value === leaveType)?.label ?? leaveType;
  const key = String(label).trim().toLowerCase();
  const row = balance.find((b) => String(b.label ?? "").trim().toLowerCase() === key);
  if (!row || row.is_paid === false || row.tracked === false || row.available == null) return null;
  return row;
}

/**
 * Leave Application Modal
 * @param {Object} props
 * @param {Function} props.onRefresh - Callback function to refresh the leave application list
 * @returns {JSX.Element}
 */
export default function LeaveApplicationModal({ onRefresh = () => {} }: { onRefresh?: () => void }) {
  const { user } = useAuth();
  const employeeId = user?.employee_id;

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [balance, setBalance] = useState<LeaveBalanceRow[]>([]);

  const methods = useForm({ defaultValues: LEAVE_APPLICATION_DEFAULT_VALUES });
  const { reset, handleSubmit, watch, setError } = methods;
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const leaveType = watch("leaveType");
  const checkedBalance = findCheckedBalance(balance, leaveType);

  useEffect(() => {
    if (!open) return;
    reset(LEAVE_APPLICATION_DEFAULT_VALUES);
  }, [open, reset]);

  // Fresh figures each time the form opens; if they can't be read the check is skipped.
  useEffect(() => {
    if (!open || !employeeId) return;
    let cancelled = false;
    getMyLeaveBalance(employeeId as number)
      .then((rows) => {
        if (!cancelled) setBalance(rows);
      })
      .catch(() => {
        if (!cancelled) setBalance([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, employeeId]);

  useEffect(() => {
    if (!endDate) {
      methods.setValue("endSession", "");
    }
  }, [endDate, methods]);

  const submitLeaveApplication = async (data) => {
    if (!employeeId) return;

    const row = findCheckedBalance(balance, data.leaveType);
    if (row) {
      const days = calculateLeaveDuration({
        startDate: data.startDate,
        startSession: data.startSession,
        endDate: data.endDate,
        endSession: data.endSession,
      });
      if (days > Number(row.available)) {
        setError("leaveType", {
          type: "balance",
          message: `Only ${row.available} day(s) of ${row.label} available; this request is ${days} day(s)`,
        });
        return;
      }
    }

    setSaving(true);

    try {
      await applyForLeave(employeeId, data);
      toast.success("Leave Application successful");
      setOpen(false);
      onRefresh?.();
    } catch (err) {
      toast.error(
        logError({
          message: "Failed to apply for leave",
          error: err,
          code: "LEAVE_APPLICATION_FAILED",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn cta-outlined-btn"
        onClick={() => setOpen(true)}
      >
        + Add Leave
      </button>

      <ModalWrapper
        open={open}
        onClose={() => {
          if (!saving) setOpen(false);
        }}
        maxWidth="md"
      >
        <FormProvider {...methods}>
          <form
            className="column gap-1"
            onSubmit={handleSubmit(submitLeaveApplication)}
          >
            <SectionTitle title="Applying for leave" />

            <ControlledSelect
              id="leaveType"
              name="leaveType"
              label="Leave type*"
              placeholder="Select leave type"
              rules={leaveApplicationValidation.leaveType}
              fullWidth
              options={LEAVE_TYPE_OPTIONS}
              reserveErrorSpace
            />
            {checkedBalance && (
              <span className="text-sm">Available: {checkedBalance.available} day(s)</span>
            )}

            <div className="column gap-1">
              <span>Start date*</span>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}
              >
                <ControlledDatePicker
                  name="startDate"
                  id="startDate"
                  disablePast
                  minDate={dayjs()}
                  rules={leaveApplicationValidation.startDate}
                  reserveErrorSpace
                />

                <ControlledSelect
                  id="startSession"
                  name="startSession"
                  placeholder="Select start date session"
                  rules={leaveApplicationValidation.startSession}
                  options={LEAVES_SESSION_OPTIONS}
                  reserveErrorSpace
                />
              </Box>
            </div>

            <div className="column gap-1">
              <span>End date</span>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}
              >
                <ControlledDatePicker
                  name="endDate"
                  id="endDate"
                  disablePast
                  minDate={
                    startDate
                      ? dayjs(startDate).add(1, "day")
                      : dayjs().add(1, "day")
                  }
                  rules={leaveApplicationValidation.endDate(startDate)}
                  reserveErrorSpace
                />
                <ControlledSelect
                  id="endSession"
                  name="endSession"
                  placeholder="Select end date session"
                  rules={leaveApplicationValidation.endSession}
                  options={LEAVES_SESSION_OPTIONS}
                  reserveErrorSpace
                />
              </Box>
            </div>

            <ControlledTextField
              id="reason"
              name="reason"
              label="Reason"
              placeholder="Enter a reason"
              rules={leaveApplicationValidation.reason}
              isMultiline
              rows={3}
              showCharCount
              fullWidth
            />

            <ControlledFileUpload
              name="attachment"
              rules={leaveApplicationValidation.attachment}
              autoUpload={false}
              buttonText="Attach file"
              buttonIcon="ion:attach-outline"
              buttonClass="btn text-btn text-btn--medium underline"
              showPreview
              previewPosition="after"
              previewText="view"
            />

            <div className="row row--end gap-3">
              <button
                type="button"
                className="btn outlined-btn"
                onClick={() => setOpen(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button type="submit" className="btn cta-btn" disabled={saving}>
                Save
              </button>
            </div>
          </form>
        </FormProvider>
      </ModalWrapper>
    </>
  );
}
